/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/scope-sheet-columns.ts
 * PURPOSE: Column band configuration and grid style helpers for the scope sheet.
 */

import type { Column, ColumnBand, CustomColumnDef } from './ScopeSheetGridTypes'
import type { GridViewPreset } from '../BuilderWorkspace'

/**
 * Default baseline + advanced opt-in columns.
 *
 * The first 9 bands (Identity → Outcome) hold the mandatory 17-column
 * baseline that ships every Bravada quote — these positions are stable
 * because LineItemRow renders them with hand-coded styling. Adding optional
 * columns mid-band would shift the hand-coded cell indices and silently
 * break cursor navigation, so all opt-in extensions live in the trailing
 * "Advanced" band instead.
 *
 * The "Advanced" band is the SAM-parity expansion. Every column there is
 * `optional: true`. Visibility is driven by `quote.config.visible_columns`
 * with three-state semantics:
 *   - key absent  → ALL optional columns visible (Excel-grade default)
 *   - empty array → user explicitly hid all (preserved)
 *   - id list     → exactly those visible
 * See QB-SCOPESHEET-VISIBLE-001 (visible-columns-helper.ts) for the
 * resolver used at render time and in the Setup picker.
 */
export const BASE_COLUMN_BANDS: ColumnBand[] = [
  {
    id: 'identity',
    label: 'Identity',
    color: 'slate',
    bgClass: 'bg-slate-50 dark:bg-slate-900/50',
    textClass: 'text-slate-600 dark:text-slate-400',
    columns: [
      { id: 'item_number', label: '#', width: 36, align: 'center', computed: true },
      { id: 'location', label: 'Location', width: 80, editable: true },
      { id: 'description', label: 'Description', width: 220, editable: true },
    ],
  },
  {
    id: 'system',
    label: 'System',
    color: 'indigo',
    bgClass: 'bg-indigo-50/50 dark:bg-indigo-950/30',
    textClass: 'text-indigo-600 dark:text-indigo-400',
    columns: [
      { id: 'application', label: 'Application', width: 100, editable: true },
    ],
  },
  {
    id: 'measure',
    label: 'Measure',
    color: 'cyan',
    bgClass: 'bg-cyan-50/50 dark:bg-cyan-950/30',
    textClass: 'text-cyan-600 dark:text-cyan-400',
    columns: [
      { id: 'quantity', label: 'Qty', width: 60, align: 'right', editable: true },
      { id: 'unit', label: 'Unit', width: 45, align: 'center', editable: true },
    ],
  },
  {
    id: 'labour',
    label: 'Labour',
    color: 'blue',
    bgClass: 'bg-blue-50/50 dark:bg-blue-950/30',
    textClass: 'text-blue-600 dark:text-blue-400',
    columns: [
      { id: 'productivity_pmpd', label: 'PMPD', width: 50, align: 'right', editable: true },
      { id: 'labour_hours', label: 'Hrs', width: 40, align: 'right', computed: true },
      { id: 'labour_days', label: 'Days', width: 45, align: 'right', computed: true },
      { id: 'labour_chargeout', label: 'C/O', width: 75, align: 'right', editable: true },
    ],
  },
  {
    id: 'rates',
    label: 'Rates',
    color: 'yellow',
    bgClass: 'bg-yellow-50/50 dark:bg-yellow-950/30',
    textClass: 'text-yellow-700 dark:text-yellow-400',
    columns: [
      { id: 'rate_per_unit', label: 'Calc', width: 55, align: 'right', computed: true },
      { id: 'adjusted_rate', label: 'Adj', width: 55, align: 'right', override: true, editable: true },
    ],
  },
  {
    id: 'products',
    label: 'Products',
    color: 'amber',
    bgClass: 'bg-amber-50/50 dark:bg-amber-950/30',
    textClass: 'text-amber-600 dark:text-amber-400',
    columns: [
      { id: 'material_chargeout', label: 'C/O', width: 75, align: 'right', editable: true },
    ],
  },
  {
    id: 'plant',
    label: 'Plant',
    color: 'purple',
    bgClass: 'bg-purple-50/50 dark:bg-purple-950/30',
    textClass: 'text-purple-600 dark:text-purple-400',
    columns: [
      { id: 'plant_chargeout', label: 'C/O', width: 65, align: 'right', editable: true },
    ],
  },
  {
    id: 'subcon',
    label: 'Subcon',
    color: 'teal',
    bgClass: 'bg-teal-50/50 dark:bg-teal-950/30',
    textClass: 'text-teal-600 dark:text-teal-400',
    columns: [
      { id: 'subcon_chargeout', label: 'C/O', width: 65, align: 'right', editable: true },
    ],
  },
  {
    id: 'outcome',
    label: 'Outcome',
    color: 'emerald',
    bgClass: 'bg-emerald-50/50 dark:bg-emerald-950/30',
    textClass: 'text-emerald-600 dark:text-emerald-400',
    columns: [
      { id: 'margin_percent', label: '%', width: 45, align: 'center', computed: true },
      { id: 'subtotal', label: 'Total', width: 85, align: 'right', computed: true },
    ],
  },

  // ---------------------------------------------------------------------------
  // ADVANCED — 50-column SAM-parity expansion. Every column here is opt-in.
  // The grid keeps a track for each (width=0 + hidden when off) so positional
  // indices stay stable even when the user toggles visibility on/off.
  // ---------------------------------------------------------------------------
  {
    id: 'advanced',
    label: 'Advanced',
    color: 'rose',
    bgClass: 'bg-rose-50/40 dark:bg-rose-950/30',
    textClass: 'text-rose-600 dark:text-rose-400',
    columns: [
      // Identity extensions
      { id: 'product_system', label: 'Product / System', width: 140, editable: true, optional: true, type: 'text' },
      { id: 'is_client_responsibility', label: 'Client resp.', width: 70, align: 'center', editable: true, optional: true, type: 'text' },
      // Labour cost (vs chargeout)
      { id: 'labour_cost', label: 'Labour Cost', width: 90, align: 'right', editable: true, optional: true, type: 'currency' },
      // Material cost (vs chargeout)
      { id: 'material_cost', label: 'Mat Cost', width: 80, align: 'right', editable: true, optional: true, type: 'currency' },
      // Plant breakdown
      { id: 'plant_description', label: 'Plant Desc', width: 140, editable: true, optional: true, type: 'text' },
      { id: 'plant_unit_cost', label: 'Plant $/u', width: 75, align: 'right', editable: true, optional: true, type: 'currency' },
      { id: 'plant_quantity', label: 'Plant Qty', width: 70, align: 'right', editable: true, optional: true, type: 'number' },
      { id: 'plant_cost', label: 'Plant Cost', width: 80, align: 'right', editable: true, optional: true, type: 'currency' },
      // Subcon breakdown
      { id: 'subcon_description', label: 'Subcon Desc', width: 140, editable: true, optional: true, type: 'text' },
      { id: 'subcon_quote', label: 'Subcon $', width: 80, align: 'right', editable: true, optional: true, type: 'currency' },
      { id: 'subcon_days', label: 'Subcon Days', width: 80, align: 'right', editable: true, optional: true, type: 'number' },
      { id: 'subcon_cost', label: 'Subcon Cost', width: 80, align: 'right', editable: true, optional: true, type: 'currency' },
      // Notes
      { id: 'internal_notes', label: 'Internal Notes', width: 200, editable: true, optional: true, type: 'text' },
      { id: 'client_visible_notes', label: 'Client Notes', width: 200, editable: true, optional: true, type: 'text' },
      // Outcome derivatives
      { id: 'total_cost', label: 'Total Cost', width: 90, align: 'right', computed: true, optional: true, type: 'currency' },
      { id: 'chargeout_total', label: 'Total C/O', width: 90, align: 'right', computed: true, optional: true, type: 'currency' },
      { id: 'margin', label: 'Margin $', width: 80, align: 'right', computed: true, optional: true, type: 'currency' },
    ],
  },
]

/**
 * Apply a per-quote visibility map to the column bands.
 *
 * Pure I→O. Inputs are the BASE bands and the array of column ids the user
 * has opted into via Setup. Output is a new band array where:
 *   - Mandatory columns (no `optional` flag) stay as-is.
 *   - Optional columns become visible iff their id is in `visibleIds`.
 *   - Optional columns NOT in `visibleIds` get `hidden: true, width: 0`.
 *
 * Why hidden vs filtered: `LineItemRow` / `EditableCell` index columns by
 * position when navigating, so dropping rows mid-list would scramble the
 * keyboard cursor mapping. Setting width=0 + hidden keeps positions stable.
 */
export function applyOptionalColumnVisibility(
  bands: ColumnBand[],
  visibleIds: Iterable<string>,
): ColumnBand[] {
  const allowed = new Set(visibleIds)
  return bands.map((band) => ({
    ...band,
    columns: band.columns.map((col) => {
      if (!col.optional) return col
      if (allowed.has(col.id)) return { ...col, hidden: false }
      return { ...col, hidden: true, width: 0 }
    }),
  }))
}

/** Build a CSS grid-template-columns value from column definitions. */
export function getGridColsStyle(columns: Column[]): React.CSSProperties {
  const widths = columns.map((col) => {
    if (col.hidden) return '0px'
    if (col.width === 'flex') return 'minmax(180px, 1fr)'
    return `${col.width}px`
  })
  return { gridTemplateColumns: widths.join(' ') }
}

/** Apply a view preset (full / compact) to column bands. */
export function applyGridViewPreset(preset: GridViewPreset, bands: ColumnBand[]): ColumnBand[] {
  if (preset === 'full') return bands

  const hiddenIds = new Set<string>([
    'application',
    'labour_hours',
    'labour_days',
    'rate_per_unit',
    'adjusted_rate',
    'margin_percent',
  ])

  return bands.map((band) => ({
    ...band,
    columns: band.columns.map((col) => {
      if (hiddenIds.has(col.id)) {
        return { ...col, hidden: true, width: 0 }
      }
      if (col.id.endsWith('_chargeout')) {
        return { ...col, width: Math.max(typeof col.width === 'number' ? col.width : 0, 85) }
      }
      if (col.id === 'description') {
        return { ...col, width: 180 }
      }
      return col
    }),
  }))
}

/** Check whether a column id is a numeric field. */
export function isNumericColumn(colId: string): boolean {
  return (
    colId === 'quantity' ||
    colId === 'productivity_pmpd' ||
    colId === 'adjusted_rate' ||
    colId === 'labour_cost' ||
    colId === 'labour_chargeout' ||
    colId === 'material_cost' ||
    colId === 'material_chargeout' ||
    colId === 'plant_unit_cost' ||
    colId === 'plant_quantity' ||
    colId === 'plant_cost' ||
    colId === 'plant_chargeout' ||
    colId === 'subcon_quote' ||
    colId === 'subcon_days' ||
    colId === 'subcon_cost' ||
    colId === 'subcon_chargeout' ||
    colId === 'total_cost' ||
    colId === 'chargeout_total' ||
    colId === 'margin'
  )
}

/**
 * Build a "Custom" band from a quote's user-defined custom columns. Returns
 * null when there are zero custom columns (so the band is omitted entirely
 * — no empty header strip, no extra grid track). Sort order: respects
 * sort_order if set, else falls back to declaration order.
 */
export function buildCustomColumnBand(
  customColumns: CustomColumnDef[] | undefined,
): ColumnBand | null {
  if (!customColumns || customColumns.length === 0) return null
  const sorted = [...customColumns].sort((a, b) => {
    const ao = a.sort_order ?? 1_000_000
    const bo = b.sort_order ?? 1_000_000
    if (ao !== bo) return ao - bo
    return a.key.localeCompare(b.key)
  })
  const columns: Column[] = sorted.map((cc) => ({
    id: cc.key,
    label: cc.label || cc.key,
    width: cc.width && cc.width > 0 ? cc.width : 90,
    align: cc.type === 'number' || cc.type === 'currency' ? 'right' : 'left',
    editable: true,
    custom: true,
    type: cc.type ?? 'text',
    defaultValue: cc.default_value ?? null,
  }))
  return {
    id: 'custom',
    label: 'Custom',
    color: 'rose',
    bgClass: 'bg-rose-50/50 dark:bg-rose-950/30',
    textClass: 'text-rose-600 dark:text-rose-400',
    columns,
  }
}
